import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import {
  BarList, Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput,
  fmtDate, panelGet, useAdminGet,
} from '../kit';
import { LIFECYCLE_LABEL, UserTagCells, tagText, toLifecycle } from '../tags/shared';
import HomeMonthly from './HomeMonthly';
import { RowClick } from './StepTimings';
import TechCardModal from './TechCardModal';

/**
 * Homepage searches — every search run from the public homepage's search box,
 * suggestion chips and Surprise Me, and what the visitor did next.
 *
 * Backed by admin_home_searches.py, which merges three sources into one row
 * per search and says which in `origin`: the server's own log ("server" —
 * search_query_logs rows with backend 'home:%', written by
 * /api/v1/home/search since it went live; these carry the result count and
 * the 3 cards shown), the site_events click stream ("click_stream" —
 * search_submit / search_bubble / search_surprise_me since 2026-09-08) and,
 * before that, the same clicks as PostHog saw them from 2026-05 ("posthog",
 * imported once into home_search_history_events; ids "p:<id>"). The last
 * two have the query and source only, so their result count reads "not
 * recorded". "What happened next" is the same session's events up to its
 * next homepage search (or 30 minutes): card clicks, View More, locked
 * filters, sign-up, and a single outcome.
 *
 * The list is paged and sorted on the server; the KPIs and breakdowns honour
 * the same filters. State defaults to anonymous visitors — that is the
 * question this tab exists for. Filters live in component state only, like
 * Query explorer.
 */

const EXPORT_PATH = '/api/v1/admin/search/home-searches.xlsx';

type Filters = {
  q: string; state: string; source: string; outcome: string; zero_results: string;
  country: string; from: string; to: string;
};
const DEFAULTS: Filters = {
  q: '', state: 'anonymous', source: '', outcome: '', zero_results: '', country: '', from: '', to: '',
};

const STATES = ['anonymous', 'signed_in', 'all'];
const STATE_LABELS: Record<string, string> = {
  anonymous: 'Anonymous visitors', signed_in: 'Signed-in users', all: 'Everyone',
};
const SOURCES = ['typed', 'chip', 'surprise', 'unknown'];
const SOURCE_LABELS: Record<string, string> = {
  '': 'Any source', typed: 'Typed', chip: 'Suggestion chip', surprise: 'Surprise me', unknown: 'Unknown',
};
const OUTCOMES = ['signed_up', 'opened_signup', 'clicked_result', 'view_more', 'refined', 'cleared', 'left'];
const OUTCOME_LABELS: Record<string, string> = {
  '': 'Any outcome', signed_up: 'Signed up', opened_signup: 'Opened sign-up',
  clicked_result: 'Clicked a result', view_more: 'View more', refined: 'Searched again',
  cleared: 'Cleared', left: 'Left',
};
// '1' / '0' — the admin panel's convention for a boolean filter (see Site visitors).
const ZERO_OPTIONS = ['', '1', '0'];
const ZERO_LABELS: Record<string, string> = { '': 'Any result count', '1': 'Zero results only', '0': 'With results' };
const FILTER_LABELS: Record<string, string> = {
  q: 'Search', state: 'Who', source: 'Source', outcome: 'Outcome', zero_results: 'Results',
  country: 'Country', from: 'From', to: 'To',
};

/** Table column → the server's sort key; other headers do not sort. */
const SORT_BY_COL: Record<string, string> = { at: 'at', query: 'query', outcome: 'outcome' };
// No result-count column (owner, 2026-09-27): the count stays in the row's
// drawer and behind the result-count filter.
// Lifecycle · tags (2026-09-28) is the account's, from user_tags.decorate:
// the signed-in searcher, or the account an anonymous visit later became.
const COLS = ['at', 'query', 'source', 'outcome', 'clicked', 'place', 'who', 'lifecycle_tags'];
const LABELS: Record<string, string> = {
  at: 'When', query: 'Query', source: 'Source', outcome: 'Outcome',
  clicked: 'What they clicked', place: 'Where', who: 'Who', lifecycle_tags: 'Lifecycle · tags',
};

/** The account's lifecycle + tags as text (user_tags.decorate) — what the
 *  table's own search, column filter and CSV see; the cell renders pills. */
const lifecycleText = (r: any) => {
  const lc = toLifecycle(r?.lifecycle);
  return [r?.lifecycle_label || LIFECYCLE_LABEL[lc] || lc, tagText(r)].filter(Boolean).join(' · ');
};

type Tone = 'ok' | 'warn' | 'bad' | 'mute' | 'info';
const OUTCOME_TONE: Record<string, Tone> = {
  signed_up: 'ok', opened_signup: 'warn', clicked_result: 'info', view_more: 'info',
  refined: 'mute', cleared: 'mute', left: 'mute',
};

const Pill: React.FC<{ tone: Tone; children: React.ReactNode; title?: string }> =
  ({ tone, children, title }) => {
  const look = tone === 'ok' ? 'text-green-700 bg-green-50 border-green-200'
    : tone === 'warn' ? 'text-amber-700 bg-amber-50 border-amber-200'
      : tone === 'bad' ? 'text-red-600 bg-red-50 border-red-200'
        : tone === 'info' ? 'text-secondary-500 bg-secondary-100 border-secondary-400'
          : 'text-grayscale-1200 bg-grayscale-200 border-grayscale-300';
  return <span title={title} className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${look}`}>{children}</span>;
};

const outcomePill = (o: any) => {
  const k = String(o || '');
  if (!k) return <span className="text-grayscale-1100">—</span>;
  return <Pill tone={OUTCOME_TONE[k] || 'mute'}>{OUTCOME_LABELS[k] || k}</Pill>;
};
/** Where the row came from (admin_home_searches.py `origin`). */
const ORIGIN_LABELS: Record<string, string> = {
  server: 'Server log', click_stream: 'Click stream', posthog: 'PostHog (imported)',
};
const ORIGIN_HINTS: Record<string, string> = {
  server: 'Logged by the server when the search ran — result count and cards kept',
  click_stream: 'From our own click stream (site_events) — query and source only',
  posthog: 'Imported from PostHog (history before 2026-09-08) — query and source only',
};
const sourcePill = (s: any, origin?: any) => {
  const k = String(s || 'unknown');
  const o = String(origin || '');
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap"
      title={o ? `Origin: ${ORIGIN_LABELS[o] || o} — ${ORIGIN_HINTS[o] || ''}` : undefined}>
      <Pill tone={k === 'typed' ? 'info' : 'mute'}>{SOURCE_LABELS[k] || k}</Pill>
      {o === 'posthog' && (
        <span className="text-[9px] uppercase tracking-wide rounded border border-grayscale-400 text-grayscale-1100 px-1">PostHog</span>
      )}
    </span>
  );
};

const num = (v: any): number | null => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
/** Rates come as 0–1 fractions; under 10% keeps one decimal so 3.4% is not "3%". */
const pct = (v: any): string => {
  const n = num(v);
  if (n === null) return '—';
  const p = n * 100;
  return `${p < 10 && p > 0 ? p.toFixed(1) : Math.round(p)}%`;
};
const count = (v: any): string => (num(v) ?? 0).toLocaleString();

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Keeps ILIKE searches off every keystroke. */
function useDebounced(value: string, msDelay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), msDelay);
    return () => clearTimeout(t);
  }, [value, msDelay]);
  return v;
}

const saveBlob = (blob: Blob, name: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

/** A site event's props on one line: "query: jaggery · length: 7".
 *  They arrive as an object (jsonb) or JSON text. */
const propsText = (v: any): string => {
  let o = v;
  if (typeof o === 'string') {
    try { o = JSON.parse(o); } catch { return o; }
  }
  if (o === null || o === undefined) return '';
  if (typeof o !== 'object') return String(o);
  return Object.entries(o)
    .filter(([, x]) => x !== null && x !== undefined && x !== '')
    .map(([k, x]) => `${k}: ${typeof x === 'object' ? JSON.stringify(x) : String(x)}`)
    .join(' · ');
};

/** "+12 s" / "+3 min" after the search — how long the visitor took to act. */
const after = (from?: string, to?: string): string => {
  if (!from || !to) return '';
  const s = (new Date(to).getTime() - new Date(from).getTime()) / 1000;
  if (!Number.isFinite(s) || s < 0) return '';
  if (s < 60) return `+${Math.round(s)} s`;
  if (s < 3600) return `+${Math.round(s / 60)} min`;
  return `+${(s / 3600).toFixed(1)} h`;
};

/** "3 h later" between two timeline events — the silences are part of the story. */
const gapLabel = (prev?: string, cur?: string): string => {
  if (!prev || !cur) return '';
  const s = (new Date(cur).getTime() - new Date(prev).getTime()) / 1000;
  if (!Number.isFinite(s) || s < 600) return '';
  if (s < 3600) return `${Math.round(s / 60)} min later`;
  if (s < 86400) return `${Math.round(s / 3600)} h later`;
  return `${Math.round(s / 86400)} d later`;
};

const place = (city: any, country: any): string =>
  [city, country].filter(x => x !== null && x !== undefined && x !== '').join(', ');

const clickedList = (r: any): any[] => Array.isArray(r?.clicked) ? r.clicked : [];

const resultsCell = (r: any) => {
  if (!r.recorded || num(r.total) === null) {
    return <span className="text-grayscale-1100 italic"
      title={`Logged from ${r.origin === 'posthog' ? 'PostHog' : 'the click stream'} before the server recorded homepage searches — the result count was not kept`}>
      not recorded</span>;
  }
  const t = Number(r.total);
  if (t === 0 || r.zero_results) return <Pill tone="bad" title="The engine found nothing">0 — none</Pill>;
  return <span>{t.toLocaleString()}</span>;
};

// ---------------------------------------------------------------------------
// Drawer — one search: the cards shown, what happened next, the whole visit
// ---------------------------------------------------------------------------
const Line: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 text-sm border-b border-grayscale-200 py-1 last:border-0">
    <span className="text-grayscale-1100 whitespace-nowrap">{label}</span>
    <span className="text-grayscale-1300 text-right break-words min-w-0">
      {value === null || value === undefined || value === '' ? '—' : value}
    </span>
  </div>
);

const RESULT_COLS = ['rank', 'title', 'university', 'trl'];
const RESULT_LABELS: Record<string, string> = { rank: '#', title: 'Title', university: 'University', trl: 'TRL' };

const SearchDrawer: React.FC<{ id: string; onClose: () => void; onQuery: (q: string) => void }> =
  ({ id, onClose, onQuery }) => {
  const [d, setD] = useState<any>(null);
  const [error, setError] = useState('');
  const [view, setView] = useState<'all' | 'clicks' | 'searches'>('all');
  const [copied, setCopied] = useState(false);
  const [techId, setTechId] = useState<string | null>(null);
  const hereRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    setD(null); setError(''); setView('all'); setTechId(null);
    panelGet('search', 'home_search_detail', { id }).then(({ data, error }) => {
      if (!alive) return;
      setD(data); setError(error);
    });
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !techId) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, techId]);

  // Open on this search's row in the timeline — scrolled inside the drawer, so
  // the list behind it keeps its place.
  const scrollToHere = () => {
    const el = hereRef.current, box = bodyRef.current;
    if (!el || !box) return;
    const r = el.getBoundingClientRect(), b = box.getBoundingClientRect();
    box.scrollTo({ top: box.scrollTop + (r.top - b.top) - Math.max(0, (b.height - r.height) / 3),
                   behavior: 'smooth' });
  };

  const s = d?.search || null;
  const results: any[] = Array.isArray(d?.results) ? d.results : [];
  const timeline: any[] = Array.isArray(d?.timeline) ? d.timeline : [];
  const visit = d?.visit || null;
  const clicked = clickedList(s);
  const isClick = (e: any) => String(e?.kind || '') === 'click' && !e?.is_search;
  const shown = timeline.filter(e => view === 'all' || (view === 'searches' ? e?.is_search : isClick(e)));
  const counts = {
    all: timeline.length,
    clicks: timeline.filter(isClick).length,
    searches: timeline.filter(e => e?.is_search).length,
  };

  const copy = async () => {
    const text = JSON.stringify(d, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Copy this:', text);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative h-full bg-white shadow-xl flex flex-col" style={{ width: 'min(96vw, 64rem)' }}
        onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-grayscale-300 flex items-start justify-between gap-3">
          <div className="min-w-0 flex flex-col gap-1">
            <div className="text-base font-bold text-grayscale-1300 font-grotesk break-words">
              {s ? (s.query || <span className="text-grayscale-1100 font-normal">(empty query)</span>) : 'Homepage search'}
            </div>
            {s && (
              <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
                <span title={s.at}>{fmtDate(s.at)}</span>
                <span>·</span>
                {sourcePill(s.source, s.origin)}
                {outcomePill(s.outcome)}
                <span>·</span>
                <span>{s.anonymous ? `anonymous${s.email ? ` → ${s.email}` : ''}` : (s.email || s.user_id || 'signed in')}</span>
                {place(s.city, s.country) && <><span>·</span><span>{place(s.city, s.country)}</span></>}
                <span>·</span>
                <span>{resultsCell(s)}{s.recorded && (num(s.total) ?? 0) > 0 ? ' results' : ''}</span>
                {s.engine && <span className="font-mono">{s.engine}</span>}
                {s.engine === 'home:algolia-fallback' && <Pill tone="warn" title="Typesense failed; the server answered from Algolia">fallback</Pill>}
                {s.origin && <span title={ORIGIN_HINTS[String(s.origin)] || ''}>· {ORIGIN_LABELS[String(s.origin)] || s.origin}</span>}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {s?.query && <Btn small onClick={() => onQuery(String(s.query))} title="Close this and list every search for this query">all runs</Btn>}
            {d && <Btn small onClick={copy}>{copied ? 'Copied' : 'Copy as JSON'}</Btn>}
            <Btn small onClick={onClose}>Close</Btn>
          </div>
        </div>

        <div ref={bodyRef} className="p-5 overflow-y-auto flex flex-col gap-4">
          {error && <Msg kind="error">{error}</Msg>}
          {!d && !error && <Loading label="Reading the visit…" />}
          {d && s && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                <div className="lg:col-span-3 min-w-0">
                  <Section title={s.recorded
                    ? `Cards shown (${results.length}${num(s.total) !== null ? ` of ${Number(s.total).toLocaleString()} found` : ''})`
                    : 'Cards shown'}>
                    {!s.recorded ? (
                      <div className="text-sm text-grayscale-1100">
                        Not recorded — this search predates server-side logging of the homepage, so only its
                        query and source are known ({s.origin === 'posthog' ? 'from PostHog, imported' : 'from the click stream'}).
                      </div>
                    ) : results.length === 0 ? (
                      <div className="text-sm text-grayscale-1100">No results — the visitor saw “No results found”.</div>
                    ) : (
                      <RowClick onRow={setTechId}>
                        <DataTable rows={results} columns={RESULT_COLS} labels={RESULT_LABELS} maxHeight="14rem"
                          render={{
                            rank: r => <span data-row-key={r.utech_technology_id} className="text-grayscale-1100">{r.rank ?? '—'}</span>,
                            title: r => <span title={r.title}>{r.title || r.utech_technology_id || '—'}</span>,
                            trl: r => r.trl == null || r.trl === '' ? '—' : String(r.trl),
                          }} />
                      </RowClick>
                    )}
                  </Section>
                </div>
                <div className="lg:col-span-2 min-w-0">
                  <Section title="What happened next">
                    {clicked.length === 0 ? (
                      <div className="text-sm text-grayscale-1100">
                        {s.outcome === 'left' ? 'Nothing — no click before leaving or the next search.' : 'No notable action recorded.'}
                      </div>
                    ) : (
                      <ol className="flex flex-col gap-1">
                        {clicked.map((c: any, i: number) => (
                          <li key={i} className="flex items-baseline gap-2 text-sm">
                            <span className="w-14 shrink-0 text-xs text-grayscale-1100 text-right" title={c.at}>{after(s.at, c.at) || fmtDate(c.at)}</span>
                            <span className="text-grayscale-1300 min-w-0 break-words" title={propsText(c.props) || undefined}>
                              {c.label || c.action}
                            </span>
                          </li>
                        ))}
                      </ol>
                    )}
                    <div className="text-xs text-grayscale-1100">
                      {num(s.next_actions) !== null ? `${count(s.next_actions)} event${Number(s.next_actions) === 1 ? '' : 's'} before the next search or 30 min · ` : ''}
                      outcome: {OUTCOME_LABELS[String(s.outcome)] || s.outcome || '—'}
                    </div>
                  </Section>
                </div>
              </div>

              <Section title={s.origin === 'posthog' ? 'Visit (PostHog)' : 'Visit'}>
                {!visit ? (
                  <div className="text-sm text-grayscale-1100">No visit row for this session.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <div>
                      <Line label="Who" value={visit.email || visit.user_id || (s.anonymous ? 'anonymous' : '')} />
                      <Line label="Where" value={[visit.city, visit.region, visit.country].filter(Boolean).join(', ')} />
                      <Line label="Referrer" value={visit.referrer} />
                      <Line label="Landed on" value={visit.landing_path} />
                    </div>
                    <div>
                      <Line label="First seen" value={visit.first_seen ? fmtDate(visit.first_seen) : ''} />
                      <Line label="Last seen" value={visit.last_seen ? fmtDate(visit.last_seen) : ''} />
                      <Line label="Session" value={<span className="font-mono text-xs">{s.session_id}</span>} />
                      <Line label="Visitor" value={<span className="font-mono text-xs">{s.visitor_id}</span>} />
                    </div>
                    {visit.user_agent && (
                      <div className="md:col-span-2 text-xs text-grayscale-1100 break-all pt-1">{visit.user_agent}</div>
                    )}
                  </div>
                )}
              </Section>

              <Section title="The whole visit"
                right={
                  <div className="flex gap-1 items-center">
                    <button type="button" onClick={scrollToHere}
                      className="text-xs underline underline-offset-2 text-grayscale-1200 mr-2">jump to this search ↓</button>
                    {(['all', 'clicks', 'searches'] as const).map(v => (
                      <button key={v} type="button" onClick={() => setView(v)}
                        className={`py-1 px-2.5 rounded-full text-xs font-grotesk whitespace-nowrap ${view === v
                          ? 'bg-primary-500 text-grayscale-100 font-bold'
                          : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
                        {v === 'all' ? 'All' : v === 'clicks' ? 'Clicks' : 'Searches'} ({counts[v]})
                      </button>
                    ))}
                  </div>
                }>
                {shown.length === 0 ? (
                  <div className="text-sm text-grayscale-1100 py-4 text-center">
                    {timeline.length ? 'No events of this kind.' : 'No events recorded for this session.'}
                  </div>
                ) : (
                  <div className="flex flex-col">
                    {shown.map((e, i) => {
                      const here = !!e.is_this_search;
                      const gap = gapLabel(shown[i - 1]?.at, e.at);
                      const props = propsText(e.props);
                      return (
                        <div key={i} ref={here ? hereRef : undefined}
                          className={`flex gap-3 px-2 rounded-lg ${here ? 'border-2 border-primary-500 bg-primary-50 my-1'
                            : e.is_search ? 'bg-grayscale-200' : ''}`}>
                          <div className="w-20 sm:w-32 shrink-0 py-1.5 text-[11px] text-grayscale-1100 sm:whitespace-nowrap" title={e.at}>
                            {fmtDate(e.at)}
                            {gap && <div className="text-[10px] opacity-80">{gap}</div>}
                          </div>
                          <div className="flex-1 min-w-0 py-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm text-grayscale-1300 ${here ? 'font-bold' : 'font-medium'}`}>
                                {e.action || e.kind || 'event'}
                              </span>
                              {e.kind && e.kind !== 'click' && <Pill tone="mute">{e.kind}</Pill>}
                              {here ? <Pill tone="warn">this search</Pill> : e.is_search ? <Pill tone="info">search</Pill> : null}
                              {e.path && <span className="text-[11px] font-mono text-grayscale-1100">{e.path}</span>}
                            </div>
                            {props && <div className="text-xs text-grayscale-1100 break-words">{props}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>
            </>
          )}
        </div>
        {techId && <TechCardModal id={techId} onClose={() => setTechId(null)} />}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------
const HomeSearches: React.FC = () => {
  const [f, setF] = useState<Filters>(DEFAULTS);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [dlBusy, setDlBusy] = useState(false);
  const [dlErr, setDlErr] = useState('');
  const q = useDebounced(f.q);
  const country = useDebounced(f.country);

  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));
  // Clicking the bar or KPI that is already filtering clears it.
  const toggle = (k: keyof Filters, v: string) => setF(s => ({ ...s, [k]: s[k] === v ? DEFAULTS[k] : v }));

  const filterParams = useMemo(() => ({
    q, state: f.state, source: f.source, outcome: f.outcome, zero_results: f.zero_results,
    country, from: f.from, to: f.to,
  }), [q, f.state, f.source, f.outcome, f.zero_results, country, f.from, f.to]);
  const filterKey = JSON.stringify(filterParams);

  // A new filter or sort starts again from the first page.
  useEffect(() => { setPage(0); }, [filterKey, perPage, sort]);

  const stats = useAdminGet('search', 'home_search_stats', filterParams, [filterKey, tick]);
  const list = useAdminGet('search', 'home_searches',
    { ...filterParams, page, per_page: perPage, sort: sort?.key || '', dir: sort?.dir || '' },
    [filterKey, page, perPage, sort, tick]);

  // Text versions of the list-valued columns, so the table's own row filter
  // and CSV see what the admin sees; the raw row stays on `_r` for rendering.
  const rows = useMemo(() => (list.data?.rows || []).map((r: any) => ({
    ...r,
    clicked: clickedList(r).map((c: any) => c.label || c.action).join(' → '),
    place: place(r.city, r.country),
    who: r.anonymous ? (r.email ? `anonymous → ${r.email}` : 'anonymous') : (r.email || r.user_id || 'signed in'),
    lifecycle_tags: lifecycleText(r),
    _r: r,
  })), [list.data]);
  const total: number = list.data?.total ?? 0;
  const pages: number = Math.max(1, list.data?.pages ?? 1);
  const sd = stats.data || {};
  const kpis = sd.kpis || {};

  const download = async () => {
    setDlBusy(true); setDlErr('');
    const params: Record<string, string> = {};
    Object.entries(filterParams).forEach(([k, v]) => { if (v) params[k] = String(v); });
    if (sort) { params.sort = sort.key; params.dir = sort.dir; }
    const { blob, filename, error } = await apiService.adminFetchFile(
      apiService.getStoredUserId() || '', EXPORT_PATH, params);
    setDlBusy(false);
    if (error || !blob) { setDlErr(error || 'Download failed'); return; }
    const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    saveBlob(blob, filename || `homepage-searches-${ymd}.xlsx`);
  };

  const onSortChange = (key: string, dir: 'asc' | 'desc' | '') => {
    if (!key || !dir) { setSort(null); return; }
    const k = SORT_BY_COL[key];
    if (k) setSort({ key: k, dir });
  };

  const active = (Object.keys(DEFAULTS) as (keyof Filters)[])
    .filter(k => f[k] !== DEFAULTS[k]);
  const chipValue = (k: keyof Filters): string =>
    k === 'state' ? STATE_LABELS[f.state] || f.state
      : k === 'source' ? SOURCE_LABELS[f.source] || f.source
        : k === 'outcome' ? OUTCOME_LABELS[f.outcome] || f.outcome
          : k === 'zero_results' ? ZERO_LABELS[f.zero_results] || f.zero_results
            : f[k];
  const today = iso(new Date());

  const byOutcome = (sd.by_outcome || []).map((o: any) => ({
    key: String(o.outcome || ''), label: OUTCOME_LABELS[String(o.outcome)] || String(o.outcome || '(none)'), value: num(o.n) ?? 0,
  }));
  const bySource = (sd.by_source || []).map((o: any) => ({
    key: String(o.source || ''), label: SOURCE_LABELS[String(o.source)] || String(o.source || '(none)'), value: num(o.n) ?? 0,
  }));
  const keyOf = (items: { key: string; label: string }[], label: string) => items.find(i => i.label === label)?.key || '';
  const zeroRate = kpis.zero_result_rate;

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white rounded-xl border border-grayscale-300 p-4 flex items-start justify-between gap-3 flex-wrap">
        <p className="text-xs text-grayscale-1100 max-w-3xl">
          Every search from the public homepage — the search box, the suggestion chips and Surprise Me —
          and what the visitor did in the next 30 minutes (or until their next search), back to 2026-05.
          Searches since server-side logging carry the result count and the 3 cards shown; older ones
          have the query and source only, so their results read “not recorded”: from 2026-09-08 they come
          from our click stream, before that from PostHog (imported once, marked{' '}
          <span className="text-[9px] uppercase tracking-wide rounded border border-grayscale-400 px-1">PostHog</span>).
          Hover a Source for where the row came from. Click a row for the cards, the next steps and the whole visit.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Btn small onClick={() => setTick(t => t + 1)}>Refresh</Btn>
          <Btn small kind="primary" onClick={download} disabled={dlBusy}
            title="Every row matching the filters below, with results and next steps">
            {dlBusy ? 'Preparing…' : 'Download Excel'}
          </Btn>
        </div>
      </div>
      <HomeMonthly tick={tick} onShowMonth={(from, to) => {
        setF(s => ({ ...s, from, to }));
        document.getElementById('home-searches-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />

      {dlErr && <Msg kind="error">Excel export failed: {dlErr}</Msg>}
      {stats.error && <Msg kind="error">{stats.error}</Msg>}

      <KPIRow>
        <KPI label="Searches" value={stats.loading && !stats.data ? '…' : count(kpis.searches)}
          hint={STATE_LABELS[f.state]?.toLowerCase()} />
        <KPI label="Unique visitors" value={stats.loading && !stats.data ? '…' : count(kpis.unique_visitors)} />
        <KPI label="Anonymous share" value={pct(kpis.anonymous_share)}
          onClick={() => toggle('state', f.state === 'anonymous' ? 'all' : 'anonymous')}
          active={f.state === 'anonymous'} hint={f.state === 'anonymous' ? 'showing anonymous · click for everyone' : 'click for anonymous only'} />
        <KPI label="Zero-result rate" value={zeroRate === null || zeroRate === undefined ? '—' : pct(zeroRate)}
          onClick={() => toggle('zero_results', '1')} active={f.zero_results === '1'}
          hint={zeroRate === null || zeroRate === undefined ? 'no recorded searches yet' : 'recorded searches only · click to list'} />
        <KPI label="Result click-through" value={pct(kpis.click_through)}
          onClick={() => toggle('outcome', 'clicked_result')} active={f.outcome === 'clicked_result'}
          hint="clicked a card · click to list" />
        <KPI label="Signed up after" value={pct(kpis.signup_after_search)}
          onClick={() => toggle('outcome', 'signed_up')} active={f.outcome === 'signed_up'}
          hint="click to list them" />
      </KPIRow>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section title="Outcome">
          {stats.loading && !stats.data ? <Loading /> : (
            <BarList items={byOutcome.map(({ label, value }: any) => ({ label, value }))}
              onSelect={l => { const k = keyOf(byOutcome, l); if (k) toggle('outcome', k); }}
              activeLabel={f.outcome ? OUTCOME_LABELS[f.outcome] : undefined} />
          )}
        </Section>
        <Section title="Source">
          {stats.loading && !stats.data ? <Loading /> : (
            <BarList items={bySource.map(({ label, value }: any) => ({ label, value }))}
              onSelect={l => { const k = keyOf(bySource, l); if (k) toggle('source', k); }}
              activeLabel={f.source ? SOURCE_LABELS[f.source] : undefined} />
          )}
        </Section>
        <Section title="Last 30 days">
          {stats.loading && !stats.data ? <Loading /> : (
            <div className="max-h-64 overflow-y-auto overflow-x-hidden">
              <BarList items={(sd.by_day || []).map((d: any) => ({ label: String(d.day), value: num(d.n) ?? 0 }))}
                onSelect={l => setF(s => s.from === l && s.to === l ? { ...s, from: '', to: '' } : { ...s, from: l, to: l })}
                activeLabel={f.from && f.from === f.to ? f.from : undefined} />
            </div>
          )}
        </Section>
      </div>

      <div id="home-searches-list" className="scroll-mt-4" />
      <Section title="Searches"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} search{total === 1 ? '' : 'es'}</span>
            {active.length > 0 && (
              <Btn small onClick={() => setF(DEFAULTS)}>Reset {active.length} filter{active.length === 1 ? '' : 's'}</Btn>
            )}
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide placeholder="Query text…" />
          <Select value={f.state} onChange={set('state')} options={STATES} labels={STATE_LABELS} />
          <Select value={f.source} onChange={set('source')} options={['', ...SOURCES]} labels={SOURCE_LABELS} />
          <Select value={f.outcome} onChange={set('outcome')} options={['', ...OUTCOMES]} labels={OUTCOME_LABELS} />
          <Select value={f.zero_results} onChange={set('zero_results')} options={ZERO_OPTIONS} labels={ZERO_LABELS} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.country} onChange={set('country')} placeholder="Country" />
          <span className="text-xs text-grayscale-1100">from</span>
          <TextInput type="date" value={f.from} onChange={set('from')} />
          <span className="text-xs text-grayscale-1100">to</span>
          <TextInput type="date" value={f.to} onChange={set('to')} />
          <Btn small onClick={() => setF(s => s.from === today && s.to === today ? { ...s, from: '', to: '' } : { ...s, from: today, to: today })}>
            {f.from === today && f.to === today ? 'All days' : 'Today'}
          </Btn>
          <span className="text-xs text-grayscale-1100 ml-2">sort by</span>
          <Select value={sort?.key || ''} onChange={k => setSort(k ? { key: k, dir: sort?.dir || 'desc' } : null)}
            options={['', 'at', 'query', 'outcome']}
            labels={{ '': 'Newest first (default)', at: 'When', query: 'Query', outcome: 'Outcome' }} />
          {sort && (
            <Btn small onClick={() => setSort(s => s ? { key: s.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : s)}
              title="Flip the direction">{sort.dir === 'asc' ? '↑ ascending' : '↓ descending'}</Btn>
          )}
          <Select value={perPage} onChange={setPerPage} options={['25', '50', '100', '200']}
            labels={{ '25': '25 / page', '50': '50 / page', '100': '100 / page', '200': '200 / page' }} />
        </div>
        {active.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {active.map(k => (
              <Chip key={k} label={FILTER_LABELS[k] || k} value={chipValue(k)} onClear={() => set(k)(DEFAULTS[k])} />
            ))}
          </div>
        )}
        {list.error && <Msg kind="error">{list.error}</Msg>}
        {list.loading && !list.data ? <Loading /> : (
          <RowClick onRow={setOpenId}
            className="[&_tbody_tr:has([data-tone=zero])]:bg-red-50">
            <DataTable rows={rows} columns={COLS} labels={LABELS} csvName="homepage_searches"
              serverSort={sort ? { key: sort.key, dir: sort.dir } : null} onSortChange={onSortChange}
              maxHeight="40rem"
              empty={active.length ? 'Nothing matches these filters.' : 'No homepage searches recorded yet.'}
              render={{
                at: r => (
                  <span data-row-key={r.id} data-tone={r._r.zero_results ? 'zero' : 'plain'}
                    title={`${r.at}\n${r.id}`}>{fmtDate(r.at)}</span>
                ),
                query: r => (
                  <span className="inline-block truncate max-w-[18rem] align-bottom text-grayscale-1300" title={r.query}>
                    {r.query || <span className="text-grayscale-1100">(empty)</span>}
                  </span>
                ),
                source: r => sourcePill(r.source, r._r.origin),
                outcome: r => outcomePill(r.outcome),
                clicked: r => {
                  const cs = clickedList(r._r);
                  if (!cs.length) return <span className="text-grayscale-1100">—</span>;
                  const first = cs[0];
                  return (
                    <span className="inline-flex items-center gap-1.5 max-w-[20rem]" title={r.clicked}>
                      <span className="truncate">{first.label || first.action}</span>
                      {cs.length > 1 && <span className="text-[11px] text-grayscale-1100 whitespace-nowrap">+{cs.length - 1} more</span>}
                    </span>
                  );
                },
                place: r => r.place || <span className="text-grayscale-1100">—</span>,
                who: r => r._r.anonymous
                  ? <span title={r._r.visitor_id || ''}>
                      <span className="text-grayscale-1100">anonymous</span>
                      {r._r.email && <span title="Anonymous when searching; this is the account the visit later signed up or in as"> → {r._r.email}</span>}
                    </span>
                  : <span title={r._r.user_id || ''}>{r.who}</span>,
                lifecycle_tags: r => (r._r.user_id || r._r.email) ? <span className="inline-block min-w-[12rem]"><UserTagCells row={r._r} /></span> : '',
              }}
              actions={r => <Btn small onClick={() => setOpenId(String(r.id))}>Details</Btn>} />
          </RowClick>
        )}
        {total > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-grayscale-1100">
            <span>
              Page {page + 1} of {pages} · showing {rows.length ? page * Number(perPage) + 1 : 0}–
              {page * Number(perPage) + rows.length} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <Btn small onClick={() => setPage(0)} disabled={page === 0}>« First</Btn>
              <Btn small onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹ Prev</Btn>
              <Btn small onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}>Next ›</Btn>
              <Btn small onClick={() => setPage(pages - 1)} disabled={page >= pages - 1}>Last »</Btn>
            </div>
          </div>
        )}
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Top queries">
          {stats.loading && !stats.data ? <Loading /> : (
            <DataTable rows={(sd.top_queries || []).map((t: any) => ({ ...t }))}
              columns={['query', 'n', 'avg_total', 'ctr', 'signups']}
              labels={{ query: 'Query', n: 'Searches', avg_total: 'Avg results', ctr: 'Click-through', signups: 'Sign-ups' }}
              csvName="homepage_top_queries" maxHeight="22rem" defaultSort="-n"
              empty="No searches in this range."
              render={{
                query: t => (
                  <button type="button" className="underline underline-offset-2 text-grayscale-1300 truncate max-w-[16rem] inline-block align-bottom"
                    title="List these searches" onClick={() => setF(s => ({ ...s, q: String(t.query || '') }))}>
                    {t.query || '(empty)'}
                  </button>
                ),
                avg_total: t => num(t.avg_total) === null
                  ? <span className="text-grayscale-1100 italic">not recorded</span>
                  : Number(t.avg_total).toLocaleString(undefined, { maximumFractionDigits: 1 }),
                ctr: t => pct(t.ctr),
              }} />
          )}
        </Section>
        <Section title="Zero-result queries">
          {stats.loading && !stats.data ? <Loading /> : (
            <DataTable rows={sd.zero_result_queries || []}
              columns={['query', 'n', 'last_at']}
              labels={{ query: 'Query', n: 'Times', last_at: 'Last searched' }}
              csvName="homepage_zero_result_queries" maxHeight="22rem" defaultSort="-n"
              empty="No zero-result searches recorded (historical rows have no result count)."
              render={{
                query: t => (
                  <button type="button" className="underline underline-offset-2 text-grayscale-1300 truncate max-w-[16rem] inline-block align-bottom"
                    title="List these searches" onClick={() => setF(s => ({ ...s, q: String(t.query || ''), zero_results: '1' }))}>
                    {t.query || '(empty)'}
                  </button>
                ),
              }} />
          )}
        </Section>
      </div>

      {openId !== null && (
        <SearchDrawer id={openId} onClose={() => setOpenId(null)}
          onQuery={qq => { setOpenId(null); setF(s => ({ ...s, q: qq })); }} />
      )}
    </div>
  );
};

export default HomeSearches;
